import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { StarIcon } from "@heroicons/react/24/outline";
import { StarIcon as StarIconSolid } from "@heroicons/react/24/solid";
import {
  mergeTodoWorkspaceParams,
  parseTodoMine,
  parseTodoTodayPlan,
} from "./todoWorkspaceSearchParams";

function initialsFor(name) {
  if (!name) return "·";
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "·";
  const first = parts[0][0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] || "" : "";
  return (first + last).toUpperCase() || "·";
}

const AVATAR_PALETTE = [
  "#8b5cf6", "#06b6d4", "#f59e0b", "#ec4899", "#10b981",
  "#60a5fa", "#fb923c", "#34d399", "#facc15", "#f472b6",
];

function avatarColor(seed) {
  if (seed === null || seed === undefined) return AVATAR_PALETTE[0];
  const key = String(seed);
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
}

/**
 * Compact workspace controls — star (today's plan) + user chip (my tasks).
 * Used in the mobile workspace header and desktop task filter bar.
 */
export function TodoWorkspaceQuickActions({ className = "" }) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = useSelector((state) => state.session.currentUser);

  const showMine = parseTodoMine(searchParams);
  const showTodayPlan = parseTodoTodayPlan(searchParams);

  const patchParams = useCallback(
    (patch) => {
      setSearchParams((prev) => mergeTodoWorkspaceParams(prev, patch), { replace: true });
    },
    [setSearchParams]
  );

  const person = useMemo(() => {
    const name = currentUser?.name || currentUser?.email || "";
    const seed = currentUser?.id ?? name;
    return {
      name,
      initials: initialsFor(name),
      color: avatarColor(seed),
    };
  }, [currentUser]);

  return (
    <div className={`td-workspace-actions ${className}`.trim()}>
      <button
        type="button"
        className={`td-workspace-fav ${showTodayPlan ? "is-active" : ""}`}
        aria-pressed={showTodayPlan}
        aria-label={t("todo.filterTodayPlan")}
        title={t("todo.filterTodayPlan")}
        onClick={() => patchParams({ todayPlan: !showTodayPlan })}
      >
        {showTodayPlan ? (
          <StarIconSolid className="td-workspace-fav-icon" aria-hidden />
        ) : (
          <StarIcon className="td-workspace-fav-icon" aria-hidden />
        )}
      </button>
      <button
        type="button"
        className={`td-workspace-user-tag ${showMine ? "is-active" : ""}`}
        aria-pressed={showMine}
        aria-label={t("todo.filterMine")}
        title={person.name || t("todo.filterMine")}
        onClick={() => patchParams({ mine: showMine ? false : true })}
      >
        <span
          className="td-workspace-user-avatar"
          style={{ background: person.color }}
          aria-hidden
        >
          {person.initials}
        </span>
        <span className="td-workspace-user-label">{t("todo.filterMine")}</span>
      </button>
    </div>
  );
}
